// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Scale Patterns Game
//
// Include description of object & useful info here, please!
import { scaleIntervals } from 'shared/definitions/musicTheory/scales.ts';
import InteractivePrototype from './interactivePrototype.ts';
import * as Skills from '../uTheory.Skills/skills.ts';
import utils from '../utils.ts';
import Scale from '../uTheory.Notation/scale.ts';
import { getSolfegeSystemsMapForScaleMode } from './getSolfegeSystemsMapForScaleMode.ts';

class ScalePatterns extends InteractivePrototype {
  constructor(displayDiv, options, userData) {
    super(displayDiv, options, userData);
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 'all',
      },
      letters: {},
      checkMark: {},
      progressTracker: true,
      oneTryOnly: false,
      player: {},
    };
    this.init(displayDiv, options, userData);
  }

  init(displayDiv, options, userData) {
    // now call the prototype's init function:
    super.init(displayDiv, options, userData).then(() => {
      this.options.ordinalNumbers =
        typeof options.ordinalNumbers !== 'undefined' && options.ordinalNumbers === true;
    });
  }

  generateQuestions() {
    if (typeof this.options.questionPrompt !== 'undefined') {
      this.questionPrompt = this.options.questionPrompt;
    } else {
      this.questionPrompt = 'What is the interval between {question} in {question} scale?';
    }
    // For each scale listed in skill:
    const scaleIndex = [];
    this.options.skills.forEach((skill) => {
      // Figure out which scale we have:
      switch (Skills.makeString(skill)) {
        case 'major_scales_piano':
        case 'major_scales_staff':
          scaleIndex.push({ scale: 'Major', skill, scaleTextName: 'Major' });
          break;
        case 'natural_minor_piano':
        case 'natural_minor':
          scaleIndex.push({ scale: 'naturalMinor', skill, scaleTextName: 'natural minor' });
          break;
        case 'harmonic_minor':
        case 'harmonic_minor_piano':
          scaleIndex.push({ scale: 'harmonicMinor', skill, scaleTextName: 'harmonic minor' });
          break;
        case 'melodic_minor':
        case 'melodic_minor_piano':
          scaleIndex.push({
            scale: 'melodicMinorAscending',
            skill,
            scaleTextName: 'ascending melodic minor',
          });
          scaleIndex.push({
            scale: 'melodicMinorDescending',
            skill,
            scaleTextName: 'descending melodic minor',
            descending: true,
          });
          break;
      }

      // Allow specific direction setting for melodic minor:
      if (skill === 'melodic_minor_ascending') {
        scaleIndex.push({
          scale: 'melodicMinorAscending',
          skill: 'melodic_minor_piano',
          scaleTextName: 'ascending melodic minor',
        });
      }
      if (skill === 'melodic_minor_descending') {
        scaleIndex.push({
          scale: 'melodicMinorDescending',
          skill: 'melodic_minor_piano',
          scaleTextName: 'descending melodic minor',
          descending: true,
        });
      }
    }, this);

    this.questions = [];
    // For each scale:
    scaleIndex.forEach((scaleObject) => {
      // Create a question for each scale degree.
      const intervals = scaleIntervals[scaleObject.scale];

      const openSpan = '<span class="accidentals">';

      const userSolfegeSystem = this.userData?.preferences?.solfegeMethod || 'scaleDegrees';

      console.log({ userSolfegeSystem, useSystem: this.options.useSolfegeSystem });

      for (let i = 0; i < intervals.length; i++) {
        const firstNote = i + 1;
        const secondNote = i + 2 > 7 ? 1 : i + 2;
        const questionText = [];

        // Set the scale degree text:
        // Use ordinal (1st/2nd) if required:
        if (this.options.ordinalNumbers) {
          questionText[0] = scaleObject.descending
            ? `${utils.ordinal(secondNote)} and ${utils.ordinal(firstNote)}`
            : `${utils.ordinal(firstNote)} and ${utils.ordinal(secondNote)}`;
          // Use student's solfege system, if required:
        } else if (this.options.useSolfegeSystem && userSolfegeSystem !== 'scaleDegrees') {
          const solfege = getSolfegeSystemsMapForScaleMode(
            scaleObject.scale,
            userSolfegeSystem
          ).solfege;
          questionText[0] = scaleObject.descending
            ? `${solfege[secondNote - 1]} and ${solfege[firstNote - 1]}`
            : `${solfege[firstNote - 1]} and ${solfege[secondNote - 1]}`;
        } else {
          questionText[0] = scaleObject.descending
            ? `${openSpan + secondNote}</span> and ${openSpan}${firstNote}</span>`
            : `${openSpan + firstNote}</span> and ${openSpan}${secondNote}</span>`;
        }
        questionText[1] = `${utils.aOrAn(scaleObject.scaleTextName)} ${scaleObject.scaleTextName}`;
        const interval = intervals[i];
        this.questions.push({
          text: questionText,
          interval,
          skills: [scaleObject.skill],
          scaleType: scaleObject.scale,
          degrees: [firstNote, i + 2],
          descending: scaleObject.descending,
        });
      }
    }, this);

    // Now shuffle them up, unless told otherwise:
    if (typeof this.options.noShuffle === 'undefined' || this.options.noShuffle === false) {
      utils.shuffle(this.questions);
    }

    // Set max questions:
    this.maxQuestions =
      typeof this.options.maxQuestions !== 'undefined'
        ? this.options.maxQuestions
        : this.questions.length;
    this.maxQuestions = Math.min(this.maxQuestions, this.questions.length);

    console.log(this.questions);
  }

  initializeCustomElements() {
    const hs = this.options.longNames ? 'Half Step' : 'HS';
    const ws = this.options.longNames ? 'Whole Step' : 'WS';
    const buttons = [
      { text: hs, value: 'm2', hotKey: ['h', 'H'] },
      { text: ws, value: 'M2', hotKey: ['w', 'W'] },
    ];
    if (typeof this.options.hideAugSecond === 'undefined' || this.options.hideAugSecond === false) {
      buttons.push({ text: 'Aug 2nd', value: 'A2', hotKey: ['a', 'A'] });
    }
    this.letterPanel.createCustomButtons(buttons);
  }

  addListeners() {
    this.letterPanel.addCustomButtonCallback(this.check, this);
  }

  playAnswer() {
    const { degrees, scaleType, descending } = this.currentQuestion;
    if (descending) degrees.reverse();
    const [firstDegree, secondDegree] = degrees;
    const scale = new Scale('C4', scaleType);
    const firstNote = scale.getNoteAtScaleDegree(firstDegree);
    const secondNote = scale.getNoteAtScaleDegree(secondDegree);
    this.player.play(firstNote, { startTime: 0, stopTime: 0.55 });
    this.player.play(secondNote, { startTime: 0.5, stopTime: 1.25 });
  }

  check(interval) {
    if (this.rightAnswerDelay) return;
    if (this.questions[this.questionNumber].interval === interval) {
      this.rightAnswer(500);
    } else {
      this.wrongAnswer();
    }
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.lettersDiv.style.display = 'none';
    super.gameOver();
  }
}

export default ScalePatterns;
